const DEPARTMENT_FALLBACKS = [
  "var(--ds-color-category-09-default)",
  "var(--ds-color-brand-default-strong)",
  "var(--ds-color-category-02-default)",
  "var(--ds-color-category-07-default)",
  "var(--ds-color-category-08-default)",
  "var(--ds-color-category-03-default)",
];

export function asNumber(value, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

export function normalizePlanningStatus(payload = {}) {
  const safePayload = payload && typeof payload === "object" ? payload : {};
  const dates = (Array.isArray(safePayload.dates) ? safePayload.dates : [])
    .map((date) => String(date || ""))
    .filter(Boolean);
  const departments = (Array.isArray(safePayload.departments) ? safePayload.departments : [])
    .map((department) => ({
      departmentId: department?.department_id ?? null,
      department: String(department?.department || ""),
      shorthand: String(department?.shorthand || ""),
      color: department?.color || null,
      days: dates.map((date) => {
        const day = (Array.isArray(department?.days) ? department.days : [])
          .find((item) => String(item?.date || "") === date) || {};
        return {
          date,
          hasDemand: Boolean(day.has_demand),
          required: asNumber(day.required),
          covered: asNumber(day.covered),
          planned: asNumber(day.planned),
          missing: asNumber(day.missing),
          openShifts: (Array.isArray(day.open_shifts) ? day.open_shifts : []).map((shift) => ({
            shorthand: String(shift?.shorthand || ""),
            shiftName: String(shift?.shift_name || ""),
            missing: asNumber(shift?.missing),
            hours: asNumber(shift?.hours),
          })),
        };
      }),
    }));

  return {
    startDate: String(safePayload.start_date || dates[0] || ""),
    endDate: String(safePayload.end_date || dates[dates.length - 1] || ""),
    dates,
    openShiftCount: asNumber(safePayload.open_shift_count),
    openHours: asNumber(safePayload.open_hours),
    departments,
  };
}

// Die Monatsauswertung kennt drei Stundenarten. "hours" bleibt aus
// Kompatibilitaet die Schichtstunde.
export const HOURS_SERIES = ["shift", "holiday", "absence"];

export function normalizeDailyHours(rows = []) {
  return (Array.isArray(rows) ? rows : []).map((row) => {
    const shift = asNumber(row?.shift_hours ?? row?.hours);
    const holiday = asNumber(row?.holiday_hours);
    const absence = asNumber(row?.absence_hours);
    return {
      date: String(row?.date || ""),
      shift,
      holiday,
      absence,
      total: shift + holiday + absence,
      hours: shift,
    };
  });
}

export function normalizeDashboardPayload(payload = {}) {
  const safePayload = payload && typeof payload === "object" ? payload : {};
  const monthlyComparison = safePayload.monthly_comparison || {};
  return {
    currentShifts: Array.isArray(safePayload.current_shifts) ? safePayload.current_shifts : [],
    upcomingShifts: Array.isArray(safePayload.upcoming_shifts) ? safePayload.upcoming_shifts : [],
    locationName: String(safePayload.location_name || ""),
    planningStatus: normalizePlanningStatus(safePayload.planning_status),
    departmentColorMap: safePayload.department_color_map || {},
    monthlyByDay: normalizeDailyHours(safePayload.monthly_hours_by_day),
    monthlyByDepartment: Array.isArray(safePayload.monthly_hours_by_department)
      ? safePayload.monthly_hours_by_department
      : [],
    monthlyTotal: asNumber(safePayload.monthly_total_hours),
    // Schicht- plus Ferien- und Absenzstunden. Fehlt der Wert, bleibt es bei
    // den Schichtstunden.
    monthlyBreakdownTotal: asNumber(
      safePayload.monthly_breakdown_total_hours ?? safePayload.monthly_total_hours
    ),
    previousMonthlyTotal: asNumber(
      monthlyComparison.previous_total_hours ?? safePayload.previous_month_total_hours
    ),
    monthlyChangePercent: (monthlyComparison.change_percent ?? safePayload.monthly_change_percent) == null
      ? null
      : asNumber(monthlyComparison.change_percent ?? safePayload.monthly_change_percent),
    features: safePayload.features || {},
    accessLevel: safePayload.access_level || "",
  };
}

export function monthValue(date = new Date()) {
  return date.getFullYear() + "-" + String(date.getMonth() + 1).padStart(2, "0");
}

export function shiftMonth(value, delta) {
  const [year, month] = String(value).split("-").map(Number);
  if (!Number.isInteger(year) || !Number.isInteger(month)) return monthValue();
  return monthValue(new Date(year, month - 1 + delta, 1, 12));
}

export function monthLabel(value, locale) {
  const [year, month] = String(value).split("-").map(Number);
  if (!Number.isInteger(year) || !Number.isInteger(month)) return "";
  return new Intl.DateTimeFormat(locale, { month: "long", year: "numeric" }).format(
    new Date(year, month - 1, 1, 12)
  );
}

export function initials(name) {
  return String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase() || "–";
}

export function validDate(value) {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function calendarDate(value, fallback = new Date()) {
  const normalizedValue = typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value.trim())
    ? `${value.trim()}T12:00:00`
    : value;
  return validDate(normalizedValue) || validDate(fallback) || new Date();
}

export function shiftStartDate(shift) {
  return validDate(shift?.start_at)
    || validDate(shift?.date ? shift.date + "T" + (shift.start || "00:00") : null);
}

export function sortCurrentShifts(items) {
  return [...items].sort((left, right) => {
    const leftStart = shiftStartDate(left)?.getTime() ?? Infinity;
    const rightStart = shiftStartDate(right)?.getTime() ?? Infinity;
    if (leftStart !== rightStart) return leftStart - rightStart;
    return String(left?.name || "").localeCompare(String(right?.name || ""), undefined, {
      sensitivity: "base",
    });
  });
}

export function isFeatureVisible(features, accessLevel, key) {
  if (accessLevel === "Super_Admin") return true;
  return !Object.prototype.hasOwnProperty.call(features || {}, key) || Boolean(features[key]);
}

export function createDepartmentColorResolver(colorMap = {}) {
  const normalizedColors = Object.entries(colorMap).reduce((colors, [key, value]) => {
    colors[String(key).trim().toLowerCase()] = value;
    return colors;
  }, {});

  return (department) => {
    const key = String(department || "").trim().toLowerCase();
    if (key && normalizedColors[key]) return normalizedColors[key];
    let hash = 0;
    for (let index = 0; index < key.length; index += 1) {
      hash = (hash * 31 + key.charCodeAt(index)) >>> 0;
    }
    return DEPARTMENT_FALLBACKS[hash % DEPARTMENT_FALLBACKS.length];
  };
}

export function buildCurrentDeployment(shifts, {
  unassignedLabel = "Ohne Abteilung",
  colorForDepartment = () => null,
} = {}) {
  const rows = Array.isArray(shifts) ? shifts : [];
  const byDepartment = new Map();

  rows.forEach((shift) => {
    const department = String(shift?.department || "").trim() || unassignedLabel;
    const existing = byDepartment.get(department) || { department, count: 0, color: null };
    existing.count += 1;
    existing.color = existing.color || shift?.department_color || shift?.color || null;
    byDepartment.set(department, existing);
  });

  const total = rows.length;
  const items = [...byDepartment.values()]
    .map((item) => ({
      ...item,
      color: item.color || colorForDepartment(item.department),
      percent: total ? (item.count / total) * 100 : 0,
    }))
    .sort((left, right) => (
      right.count - left.count
      || left.department.localeCompare(right.department, undefined, { sensitivity: "base" })
    ));

  return { items, total };
}

export function buildDonutSegments(items, getColor) {
  const normalized = items
    .map((item) => ({ ...item, percent: Math.max(0, asNumber(item?.percent)) }))
    .filter((item) => item.percent > 0);
  if (normalized.length === 0) {
    return [{ key: "empty", start: 0, length: 100, color: "var(--ds-color-border-default)" }];
  }

  const total = normalized.reduce((sum, item) => sum + item.percent, 0);
  let cursor = 0;
  return normalized.map((item, index) => {
    const start = cursor;
    const length = index === normalized.length - 1
      ? 100 - cursor
      : (item.percent / total) * 100;
    cursor += length;
    return {
      key: String(item.department || index) + "-" + index,
      start,
      length,
      color: getColor(item),
    };
  });
}
